package top.wcpe.allincore.dsl.ir.canvas

import kotlin.jvm.JvmStatic

/**
 * draw 字节码的元数据：opcode 分类、操作数数量、单条指令字节长度。
 *
 * 从原 [DrawBlob] 拆出（M11.x 重构）。[DrawBlob] 仍保留同名静态方法
 * 作为门面转调本类，对外 API 兼容。
 */
object DrawBlobSpec {

    /** 变长指令判定（POLYGON / PATH / PATH_BUILD）。 */
    @JvmStatic
    fun isVariadic(opcode: Byte): Boolean =
        opcode == DrawOpcodes.OP_POLYGON ||
            opcode == DrawOpcodes.OP_PATH ||
            opcode == DrawOpcodes.OP_PATH_BUILD

    /** 控制流 opcode 判定。 */
    @JvmStatic
    fun isControlFlow(opcode: Byte): Boolean =
        opcode == DrawOpcodes.OP_VAL_ASSIGN ||
            opcode == DrawOpcodes.OP_FOR_BEGIN || opcode == DrawOpcodes.OP_FOR_END ||
            opcode == DrawOpcodes.OP_IF_BEGIN || opcode == DrawOpcodes.OP_IF_END

    /** 各 opcode 的固定操作数个数（不适用于变长指令 POLYGON/PATH）。 */
    @JvmStatic
    fun expectedOperandCount(opcode: Byte): Int = when (opcode) {
        DrawOpcodes.OP_RECT -> 5
        DrawOpcodes.OP_CIRCLE, DrawOpcodes.OP_DOT -> 4
        DrawOpcodes.OP_LINE -> 6
        DrawOpcodes.OP_ARC -> 6
        DrawOpcodes.OP_RING -> 5
        DrawOpcodes.OP_ROUND_RECT -> 6
        // x, y, size, color, content(Str)
        DrawOpcodes.OP_TEXT -> 5
        // x, y, w, h, resourceId(Str)
        DrawOpcodes.OP_DRAW_IMAGE -> 5
        // p0x, p0y, c1x, c1y, p1x, p1y, width, color
        DrawOpcodes.OP_BEZIER_QUAD -> 8
        // p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, width, color
        DrawOpcodes.OP_BEZIER_CUBIC -> 10
        // v4 B.5：data(OPERAND_BYTES), srcW, srcH, paletteId, dstX, dstY, dstW, dstH
        DrawOpcodes.OP_BITMAP -> 8
        // operands[0]=slotIndex(literal), operands[1]=value
        DrawOpcodes.OP_VAL_ASSIGN -> 2
        // operands[0]=slotIndex, operands[1]=iterCount, operands[2]=bodyByteLength
        DrawOpcodes.OP_FOR_BEGIN -> 3
        // operands[0]=condition, operands[1]=thenByteLength, operands[2]=elseByteLength
        DrawOpcodes.OP_IF_BEGIN -> 3
        DrawOpcodes.OP_FOR_END, DrawOpcodes.OP_IF_END -> 0
        DrawOpcodes.OP_POLYGON, DrawOpcodes.OP_PATH, DrawOpcodes.OP_PATH_BUILD ->
            throw IllegalArgumentException(
                "opcode 0x${(opcode.toInt() and 0xFF).toString(16)} 为变长指令，无固定操作数个数",
            )
        else -> throw IllegalArgumentException(
            "未知 draw opcode: 0x${(opcode.toInt() and 0xFF).toString(16)}",
        )
    }

    /** 校验变长指令的 operands 数量下限。 */
    @JvmStatic
    fun validateVariadicOperands(opcode: Byte, operands: Array<Operand>) {
        // POLYGON: 2N 顶点 + 1 color, 至少 3 顶点（6 + 1 = 7）
        // PATH:    2N 顶点 + width + color, 至少 2 顶点（4 + 2 = 6）
        // PATH_BUILD: 节点坐标 operands + width + color，至少 2（width + color）；
        //            节点坐标 operand 数由 pathBuildKinds 决定，单独由 Instruction 自校验。
        if (opcode == DrawOpcodes.OP_POLYGON) {
            val minOps = 3 * 2 + 1
            if (operands.size < minOps || (operands.size - 1) % 2 != 0) {
                throw IllegalArgumentException(
                    "POLYGON 操作数数量非法: ${operands.size}（要求 2N+1 且 N>=3）",
                )
            }
            val vertexCount = (operands.size - 1) / 2
            if (vertexCount > 0xFF) {
                throw IllegalArgumentException("POLYGON 顶点数超过 255: $vertexCount")
            }
        } else if (opcode == DrawOpcodes.OP_PATH) {
            val minOps = 2 * 2 + 2
            if (operands.size < minOps || (operands.size - 2) % 2 != 0) {
                throw IllegalArgumentException(
                    "PATH 操作数数量非法: ${operands.size}（要求 2N+2 且 N>=2）",
                )
            }
            val vertexCount = (operands.size - 2) / 2
            if (vertexCount > 0xFF) {
                throw IllegalArgumentException("PATH 顶点数超过 255: $vertexCount")
            }
        } else {
            // OP_PATH_BUILD：至少有 width + color
            if (operands.size < 2) {
                throw IllegalArgumentException(
                    "PATH_BUILD 操作数数量非法: ${operands.size}（至少 width + color 共 2 个）",
                )
            }
        }
    }

    /**
     * v4 B.4：根据 PATH_BUILD 的节点种类计算节点坐标 operand 总数。
     *
     * cmdKind → operand 数：MoveTo/LineTo=2, QuadTo=4, CubicTo=6, Close=0。
     */
    @JvmStatic
    fun pathNodeOperandCount(cmdKind: Byte): Int = when (cmdKind) {
        DrawOpcodes.PATH_NODE_MOVE_TO, DrawOpcodes.PATH_NODE_LINE_TO -> 2
        DrawOpcodes.PATH_NODE_QUAD_TO -> 4
        DrawOpcodes.PATH_NODE_CUBIC_TO -> 6
        DrawOpcodes.PATH_NODE_CLOSE -> 0
        else -> throw IllegalArgumentException(
            "未知 PATH_BUILD 节点种类: 0x${(cmdKind.toInt() and 0xFF).toString(16)}",
        )
    }

    /**
     * 单条指令编码后的字节长度（包含 opcode 本身）。
     *
     * 编译器构造 FOR_BEGIN / IF_BEGIN 的跳转偏移时可调用本方法累加 body 字节数。
     */
    @JvmStatic
    fun instructionByteLength(ins: Instruction): Int {
        val op = ins.opcode
        val ops = ins.operandsRaw()
        return when (op) {
            DrawOpcodes.OP_FOR_END, DrawOpcodes.OP_IF_END -> 1
            // opcode(1) + slotIndex(1) + operand(value)
            DrawOpcodes.OP_VAL_ASSIGN -> 1 + 1 + operandByteLength(ops[1])
            // opcode(1) + slotIndex(1) + iterCount(2) + bodyByteLength(2)
            DrawOpcodes.OP_FOR_BEGIN -> 1 + 1 + 2 + 2
            // opcode(1) + condition(Operand) + thenLen(2) + elseLen(2)
            DrawOpcodes.OP_IF_BEGIN -> 1 + operandByteLength(ops[0]) + 2 + 2
            // opcode(1) + u8 vertexCount + 2N×Operand + colorOperand
            DrawOpcodes.OP_POLYGON -> 1 + 1 + operandByteLengthSum(ops)
            // opcode(1) + u8 vertexCount + 2N×Operand + widthOperand + colorOperand + u8 closed
            DrawOpcodes.OP_PATH -> 1 + 1 + operandByteLengthSum(ops) + 1
            DrawOpcodes.OP_PATH_BUILD -> {
                // opcode(1) + u8 cmdCount + cmdCount×(u8 kind + N×Operand) + u8 modeByte
                //   + widthOperand + colorOperand
                // ops 末两位为 width / color，其余按节点 kind 顺序对应坐标 operand
                var len = 1 + 1 + 1 // opcode + cmdCount + modeByte
                val kinds = ins.pathBuildKindsRaw()
                    ?: throw IllegalStateException("OP_PATH_BUILD 缺少 pathBuildKinds 元数据")
                len += kinds.size // 每节点 1B kind
                for (o in ops) {
                    len += operandByteLength(o)
                }
                len
            }
            else -> {
                // 图元：opcode(1) + N × Operand
                var len = 1
                for (o in ops) {
                    len += operandByteLength(o)
                }
                len
            }
        }
    }

    private fun operandByteLengthSum(ops: Array<Operand>): Int {
        var sum = 0
        for (o in ops) {
            sum += operandByteLength(o)
        }
        return sum
    }

    private fun operandByteLength(op: Operand): Int = when (op) {
        is Operand.Literal -> 1 + 8
        is Operand.Expr -> 1 + 2 + op.getByteLength()
        // v4 B.5：u8 kind + u16 byteLen + raw bytes
        is Operand.Bytes -> 1 + 2 + op.getByteLength()
        // Str
        is Operand.Str -> 1 + 2 + op.value.encodeToByteArray().size
    }
}
